import { useState } from 'react';
import { Check } from 'lucide-react';
import { DisabledHint } from '@/components/common/DisabledHint';
import { EmptyState } from '@/components/common/EmptyState';
import { TableTabs } from '@/components/common/TableTabs';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { Button } from '@/components/ui/button';
import { useConfirmBeforeLeave } from '@/hooks/useConfirmBeforeLeave';
import { MenuLabelSaveDialog } from '../components/MenuLabelSaveDialog';
import { MenuUnitEditor } from '../components/MenuUnitEditor';
import { MenuUnitList } from '../components/MenuUnitList';
import { MENU_LABEL_FILTER, type MenuLabelFilter } from '../constants/menuLabels.constants';
import { useMenuLabelsSetup } from '../hooks/useMenuLabelsSetup';

const DUPLICATE_NAME_HINT = 'Two names in one menu are the same. Change one of them first.';

export function MenuLabelsPage() {
  const setup = useMenuLabelsSetup();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const changeCount = setup.changes.length;
  const isDirty = changeCount > 0;
  const leavePageDialog = useConfirmBeforeLeave(isDirty);

  const saveButton = (
    <Button
      type="button"
      onClick={() => setIsConfirmOpen(true)}
      disabled={!isDirty || setup.hasDuplicates || setup.isSaving}
    >
      <Check className="size-4" aria-hidden />
      Save changes
      {isDirty ? (
        <span className="rounded-full bg-white/20 px-1.5 text-xs tabular-nums">{changeCount}</span>
      ) : null}
    </Button>
  );

  return (
    <>
      <div className={FIT_TO_SCREEN_PAGE_CLASS}>
        <PageHeader
          title="Menu Labels"
          search={{
            value: setup.searchText,
            onChange: setup.setSearchText,
            placeholder: 'Find a menu or page',
          }}
          actions={
            <>
              <TableTabs<MenuLabelFilter>
                tabs={[
                  {
                    id: MENU_LABEL_FILTER.all,
                    label: 'All',
                    count: setup.filterCounts[MENU_LABEL_FILTER.all],
                  },
                  {
                    id: MENU_LABEL_FILTER.renamed,
                    label: 'Renamed',
                    count: setup.filterCounts[MENU_LABEL_FILTER.renamed],
                  },
                ]}
                activeTabId={setup.filter}
                onTabChange={setup.setFilter}
                ariaLabel="Which names to show"
              />
              {isDirty && setup.hasDuplicates ? (
                <DisabledHint hint={DUPLICATE_NAME_HINT} ariaLabel="Save changes">
                  {saveButton}
                </DisabledHint>
              ) : (
                saveButton
              )}
            </>
          }
        />

        {setup.selectedUnit === undefined ? (
          <EmptyState
            kind="noResults"
            title="No menu matches"
            description="Try another word, or show all names."
            fill
          />
        ) : (
          <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[18rem_minmax(0,1fr)]">
            <MenuUnitList
              units={setup.units}
              selectedKey={setup.selectedUnit.unit.menuKey}
              searchText={setup.searchText}
              onSelect={setup.chooseUnit}
            />
            <MenuUnitEditor
              unit={setup.selectedUnit}
              searchText={setup.searchText}
              actions={setup.nameActions}
            />
          </div>
        )}
      </div>

      <MenuLabelSaveDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        changes={setup.changes}
        isSaving={setup.isSaving}
        onSave={() => {
          setup.save();
          setIsConfirmOpen(false);
        }}
      />
      {leavePageDialog}
    </>
  );
}
